/* eslint-disable react/prop-types */
import * as React from 'react';

import { useBoolean, useMemoizedFn, useMount, useRequest } from 'ahooks';
import {
  Button,
  Col,
  Divider,
  Empty,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';

import {
  CheckInputbox,
  CopyOutlinedIcon,
  DecimalInputNumber,
  DeleteOutlinedIcon,
  EditOutlinedIcon,
  MenuOutlinedIcon,
  PlusOutlinedIcon,
  PrimitiveCollapse,
  PrimitiveForm,
  QuestionCircleFilledIcon,
  UpOutlinedIcon,
} from 'components';
import {
  DND_CSS,
  dndArrayMove,
  DndSortableContext,
  useDndSortable,
  useProductAction,
  useProductValue,
} from 'context';
import { getCategory } from 'services';
import type { ProductType, SelectOption, SubProduct } from 'types';
import {
  cn,
  generateSelectOptions,
  generateSubProductTitle,
  inputRule,
  localeSortByKey,
  localizeProductType,
  localizeSubProductActivateStock,
  localizeSubProductActiveSnapPay,
  localizeSubProductActiveTorobPay,
  localizeSubProductExpressShipping,
  localizeSubProductFreeShipping,
  normalizeToString,
  numberFormat,
  randomId,
  replaceExtraSeparator,
  UNIT,
} from 'utils';

const { PRICE, WIDTH, WEIGHT } = UNIT;

const productTypes = ['Normal', 'Variable'] satisfies ProductType[];

type FieldType = Pick<
  ReturnType<typeof useProductValue>,
  'productType' | 'variableAttributes'
>;

type ModalFieldType = Partial<SubProduct & Record<string, string | string[]>>;

type ProductModelsInfoSubProductProps = SubProduct &
  Record<'openModal', () => void>;

const ProductModelsInfoSubProduct =
  React.memo<ProductModelsInfoSubProductProps>(
    ({
      id,
      identifier,
      abstraction,
      originalPrice,
      price,
      purchaseMinStock,
      purchaseMaxStock,
      activateStock,
      activeSnapPay,
      activeTorobPay,
      freeShipping,
      expressShipping,
      stock,
      weight,
      height,
      width,
      length,
      attributes,
      openModal,
    }) => {
      const {
        attributes: sortableAttributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
      } = useDndSortable({
        id,
      });
      const { name, productType, subProducts = [] } = useProductValue();
      const action = useProductAction();

      const modelName = generateSubProductTitle({
        name,
        productType,
        attributes: localeSortByKey(attributes, 'name'),
      });
      const disableDuplicateSubProduct = productType === 'Normal';
      const defaultButtonProps: React.ComponentPropsWithoutRef<typeof Button> =
        {
          type: 'text',
          size: 'small',
        };
      const defaultColProps: React.ComponentPropsWithoutRef<typeof Col> = {
        span: 24,
        lg: 12,
        className: 'first:[&>span>span]:text-gray-400',
      };

      return (
        <PrimitiveCollapse
          ref={setNodeRef}
          collapsible='icon'
          style={{
            transform: DND_CSS.Transform.toString(transform),
            transition,
            ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
          }}
          expandIcon={({ isActive }) => (
            <UpOutlinedIcon
              className={cn(isActive ? 'rotate-0' : 'rotate-180')}
            />
          )}
          items={[
            {
              key: id,
              label: (
                <Space align='start' className='me-4'>
                  <MenuOutlinedIcon {...sortableAttributes} {...listeners} />
                  <Typography.Paragraph className='mb-0'>
                    {typeof modelName === 'string' ? (
                      <span>{modelName}</span>
                    ) : Array.isArray(modelName) ? (
                      modelName.map(({ name, value }, index) => {
                        const id = randomId();
                        return (
                          <React.Fragment key={id}>
                            <Typography.Text type='secondary'>
                              {index !== 0 ? <span> | </span> : null}
                              <span>{name}: </span>
                            </Typography.Text>
                            <span>{value}</span>
                          </React.Fragment>
                        );
                      })
                    ) : null}
                    {identifier ? (
                      <Tag className='ms-2'>{identifier}</Tag>
                    ) : null}
                  </Typography.Paragraph>
                </Space>
              ),
              extra: (
                <Space size={4}>
                  <Tooltip title='حذف مدل'>
                    <Popconfirm
                      title='تأیید حذف مدل'
                      description='با حذف این مدل، تمام اطلاعات مرتبط نیز پاک می‌شوند.'
                      okButtonProps={{
                        className: 'bg-[--ant-color-error]',
                      }}
                      icon={
                        <QuestionCircleFilledIcon className='text-[--ant-color-error]' />
                      }
                      onConfirm={() => {
                        const updatedSubProducts = subProducts.filter(
                          subProduct => subProduct.id !== id,
                        );

                        action({
                          type: 'UPDATE_SUB_PRODUCTS',
                          payload: {
                            subProducts: updatedSubProducts,
                            selectedSubProduct: undefined,
                          },
                        });
                      }}
                    >
                      <Button
                        icon={<DeleteOutlinedIcon />}
                        {...defaultButtonProps}
                      />
                    </Popconfirm>
                  </Tooltip>
                  <Tooltip title='کپی مدل'>
                    <Popconfirm
                      title='تأیید کپی مدل'
                      description='با کپی این مدل، یک نسخه مشابه از آن ایجاد می‌شود.'
                      onConfirm={() => {
                        const duplicatedSubProduct = subProducts.find(
                          subProduct => subProduct.id === id,
                        );

                        if (duplicatedSubProduct) {
                          action({
                            type: 'UPDATE_SUB_PRODUCTS',
                            payload: {
                              subProducts: [
                                ...subProducts,
                                {
                                  ...duplicatedSubProduct,
                                  id: randomId(),
                                  identifier: undefined,
                                },
                              ],
                              selectedSubProduct: undefined,
                            },
                          });
                        }
                      }}
                    >
                      <Button
                        icon={<CopyOutlinedIcon />}
                        disabled={disableDuplicateSubProduct}
                        {...defaultButtonProps}
                      />
                    </Popconfirm>
                  </Tooltip>
                  <Tooltip title='ویرایش مدل'>
                    <Button
                      icon={<EditOutlinedIcon />}
                      onClick={() => {
                        const selectedSubProduct = subProducts.find(
                          subProduct => subProduct.id === id,
                        );

                        if (selectedSubProduct) {
                          action({
                            type: 'UPDATE_SUB_PRODUCTS',
                            payload: {
                              subProducts,
                              selectedSubProduct,
                            },
                          });
                          openModal();
                        }
                      }}
                      {...defaultButtonProps}
                    />
                  </Tooltip>
                </Space>
              ),
              children: (
                <Row gutter={[16, 20]}>
                  <React.Fragment>
                    {Array.isArray(modelName) ? (
                      <React.Fragment>
                        {modelName.map(({ name, value }) => {
                          const id = randomId();

                          return (
                            <Col key={id} {...defaultColProps}>
                              <Typography.Text>
                                <span>{name}: </span>
                                <span>{value}</span>
                              </Typography.Text>
                            </Col>
                          );
                        })}
                        <Divider dashed className='my-1' />
                      </React.Fragment>
                    ) : null}
                  </React.Fragment>
                  <Col {...defaultColProps}>
                    <Typography.Text>
                      <span>شناسه: </span>
                      <span>{identifier || '-'}</span>
                    </Typography.Text>
                  </Col>
                  <Col {...defaultColProps}>
                    <Typography.Text>
                      <span>توضیحات مختصر: </span>
                      <span>{abstraction || '-'}</span>
                    </Typography.Text>
                  </Col>
                  <Divider dashed className='my-1' />
                  <Col {...defaultColProps}>
                    <Typography.Text>
                      <span>قیمت اولیه: </span>
                      <span>
                        {numberFormat(originalPrice, undefined, ` ${PRICE}`)}
                      </span>
                    </Typography.Text>
                  </Col>
                  <Col {...defaultColProps}>
                    <Typography.Text>
                      <span>قیمت فروش (به مشتری): </span>
                      <span>
                        {typeof price !== 'undefined'
                          ? numberFormat(price, undefined, ` ${PRICE}`)
                          : '-'}
                      </span>
                    </Typography.Text>
                  </Col>
                  <Col {...defaultColProps}>
                    <Typography.Text>
                      <span>حداقل سفارش: </span>
                      <span>
                        {purchaseMinStock
                          ? numberFormat(purchaseMinStock)
                          : '-'}
                      </span>
                    </Typography.Text>
                  </Col>
                  <Col {...defaultColProps}>
                    <Typography.Text>
                      <span>حداکثر سفارش: </span>
                      <span>
                        {purchaseMaxStock
                          ? numberFormat(purchaseMaxStock)
                          : '-'}
                      </span>
                    </Typography.Text>
                  </Col>
                  <Col {...defaultColProps}>
                    <Typography.Text>
                      <span>موجودی انبار: </span>
                      <span>{stock ? numberFormat(stock) : '-'}</span>
                    </Typography.Text>
                  </Col>
                  <Col {...defaultColProps}>
                    <Typography.Text>
                      <span>فعال‌سازی انبار: </span>
                      <span>
                        {localizeSubProductActivateStock(activateStock).label}
                      </span>
                    </Typography.Text>
                  </Col>
                  <Col {...defaultColProps}>
                    <Typography.Text>
                      <span>فعال‌سازی اسنپ‌پی: </span>
                      <span>
                        {localizeSubProductActiveSnapPay(activeSnapPay).label}
                      </span>
                    </Typography.Text>
                  </Col>
                  <Col {...defaultColProps}>
                    <Typography.Text>
                      <span>فعال‌سازی ترب‌پی: </span>
                      <span>
                        {localizeSubProductActiveTorobPay(activeTorobPay).label}
                      </span>
                    </Typography.Text>
                  </Col>
                  <Divider dashed className='my-1' />
                  <Col {...defaultColProps}>
                    <Typography.Text>
                      <span>ارسال رایگان: </span>
                      <span>
                        {localizeSubProductFreeShipping(freeShipping).label}
                      </span>
                    </Typography.Text>
                  </Col>
                  <Col {...defaultColProps}>
                    <Typography.Text>
                      <span>ارسال فوری: </span>
                      <span>
                        {
                          localizeSubProductExpressShipping(expressShipping)
                            .label
                        }
                      </span>
                    </Typography.Text>
                  </Col>
                  <Divider dashed className='my-1' />
                  <Col {...defaultColProps}>
                    <Typography.Text>
                      <span>فعال‌سازی ترب‌پی: </span>
                      <span>
                        {localizeSubProductActiveTorobPay(activeTorobPay).label}
                      </span>
                    </Typography.Text>
                  </Col>
                  <Col {...defaultColProps}>
                    <Typography.Text>
                      <span>طول: </span>
                      <span>
                        {typeof length === 'number'
                          ? numberFormat(length, undefined, ` ${WIDTH}`)
                          : '-'}
                      </span>
                    </Typography.Text>
                  </Col>
                  <Col {...defaultColProps}>
                    <Typography.Text>
                      <span>عرض: </span>
                      <span>
                        {typeof width === 'number'
                          ? numberFormat(width, undefined, ` ${WIDTH}`)
                          : '-'}
                      </span>
                    </Typography.Text>
                  </Col>
                  <Col {...defaultColProps}>
                    <Typography.Text>
                      <span>ارتفاع: </span>
                      <span>
                        {typeof height === 'number'
                          ? numberFormat(height, undefined, ` ${WIDTH}`)
                          : '-'}
                      </span>
                    </Typography.Text>
                  </Col>
                  <Col {...defaultColProps}>
                    <Typography.Text>
                      <span>وزن: </span>
                      <span>
                        {typeof weight === 'number'
                          ? numberFormat(weight, undefined, ` ${WEIGHT}`)
                          : '-'}
                      </span>
                    </Typography.Text>
                  </Col>
                </Row>
              ),
            },
          ]}
        />
      );
    },
  );
ProductModelsInfoSubProduct.displayName = 'ProductModelsInfoSubProduct';

type ProductModelsInfoModalProps = {
  open: boolean;
  closeModal: () => void;
};

const ProductModelsInfoModal = React.memo<ProductModelsInfoModalProps>(
  ({ open, closeModal }) => {
    const [form] = Form.useForm<ModalFieldType>();
    const {
      name,
      productType,
      selectedSubProduct,
      subProducts = [],
      variableAttributes = [],
    } = useProductValue();
    const action = useProductAction();

    const mainVariableAttributes: SubProduct['attributes'] = localeSortByKey(
      selectedSubProduct
        ? selectedSubProduct.attributes
        : variableAttributes.map(attribute => ({
            ...attribute,
            selectedValues: [],
          })),
      'name',
    );
    const modelName = generateSubProductTitle({
      name,
      productType,
      attributes: mainVariableAttributes,
    });

    const onCancel = useMemoizedFn(() => {
      action({
        type: 'UPDATE_SUB_PRODUCTS',
        payload: {
          subProducts,
          selectedSubProduct: undefined,
        },
      });
      form.resetFields();
      closeModal();
    });

    const onFinish = useMemoizedFn(
      ({
        id = '',
        identifier = '',
        abstraction = '',
        originalPrice = 0,
        price,
        purchaseMinStock,
        purchaseMaxStock,
        activateStock,
        activeSnapPay,
        activeTorobPay,
        freeShipping,
        expressShipping,
        warnThresholdStock,
        stock,
        width,
        length,
        height,
        weight,
        attributes = [],
        priceHistories = [],
        ...otherValues
      }: ModalFieldType) => {
        const defaultSubProducts = selectedSubProduct
          ? subProducts.filter(
              subProduct => subProduct.id !== selectedSubProduct.id,
            )
          : subProducts;
        const newSubProduct: NonNullable<typeof selectedSubProduct> = {
          id: selectedSubProduct ? selectedSubProduct.id : randomId(),
          identifier: replaceExtraSeparator(identifier),
          abstraction,
          originalPrice,
          price,
          purchaseMinStock,
          purchaseMaxStock,
          activateStock,
          activeSnapPay,
          activeTorobPay,
          freeShipping,
          expressShipping,
          warnThresholdStock,
          stock,
          width,
          length,
          height,
          weight,
          priceHistories,
          attributes: variableAttributes.map(({ id, ...otherAttributes }) => {
            const selectedValues =
              otherValues[`variable-attribute-${id}`] ?? [];

            return {
              ...otherAttributes,
              id,
              selectedValues:
                typeof selectedValues === 'string'
                  ? [selectedValues]
                  : selectedValues,
            };
          }),
        };

        action({
          type: 'UPDATE_SUB_PRODUCTS',
          payload: {
            subProducts: [newSubProduct, ...defaultSubProducts],
            selectedSubProduct: undefined,
          },
        });
        form.resetFields();
        closeModal();
      },
    );

    const { identifier } = selectedSubProduct ?? {};

    return (
      <Modal
        centered
        destroyOnClose
        width={800}
        title={
          <div className='font-medium'>
            {selectedSubProduct ? (
              <React.Fragment>
                {typeof modelName === 'string' ? (
                  <span>{modelName}</span>
                ) : Array.isArray(modelName) ? (
                  modelName.map(({ name, value }, index) => {
                    const id = randomId();
                    return (
                      <React.Fragment key={id}>
                        <Typography.Text type='secondary'>
                          {index !== 0 ? <span> | </span> : null}
                          <span>{name}: </span>
                        </Typography.Text>
                        <span>{value}</span>
                      </React.Fragment>
                    );
                  })
                ) : null}
                {identifier ? <Tag className='ms-1'>{identifier}</Tag> : null}
              </React.Fragment>
            ) : (
              <span>افزودن مدل جدید</span>
            )}
          </div>
        }
        okText={selectedSubProduct ? 'ذخیره تغییرات' : 'ساخت مدل'}
        open={open}
        onOk={form.submit}
        onCancel={onCancel}
      >
        <Divider />
        <PrimitiveForm
          className='max-h-[calc(100dvh-15rem)] overflow-y-auto'
          form={form}
          fields={
            selectedSubProduct && [
              { name: 'identifier', value: selectedSubProduct.identifier },
              { name: 'abstraction', value: selectedSubProduct.abstraction },
              {
                name: 'originalPrice',
                value: selectedSubProduct.originalPrice,
              },
              { name: 'price', value: selectedSubProduct.price },
              {
                name: 'purchaseMinStock',
                value: selectedSubProduct.purchaseMinStock,
              },
              {
                name: 'purchaseMaxStock',
                value: selectedSubProduct.purchaseMaxStock,
              },
              {
                name: 'activateStock',
                value: selectedSubProduct.activateStock,
              },
              {
                name: 'activeSnapPay',
                value: selectedSubProduct.activeSnapPay,
              },
              {
                name: 'activeTorobPay',
                value: selectedSubProduct.activeTorobPay,
              },
              {
                name: 'freeShipping',
                value: selectedSubProduct.freeShipping,
              },
              {
                name: 'expressShipping',
                value: selectedSubProduct.expressShipping,
              },
              {
                name: 'warnThresholdStock',
                value: selectedSubProduct.warnThresholdStock,
              },
              { name: 'stock', value: selectedSubProduct.stock },
              { name: 'width', value: selectedSubProduct.width },
              { name: 'length', value: selectedSubProduct.length },
              { name: 'height', value: selectedSubProduct.height },
              { name: 'weight', value: selectedSubProduct.weight },
              ...selectedSubProduct.attributes.map(
                ({ id, type, selectedValues }) => ({
                  name: `variable-attribute-${id}`,
                  value:
                    type === 'CheckBox' ? selectedValues : selectedValues[0],
                }),
              ),
            ]
          }
          onFinish={onFinish}
        >
          <Row>
            <React.Fragment>
              {productType === 'Variable' ? (
                <React.Fragment>
                  {mainVariableAttributes.map(
                    ({ id, name, type, values }, index) => {
                      const isLastIndex =
                        mainVariableAttributes.length - 1 === index;
                      const isOddLength =
                        mainVariableAttributes.length % 2 === 1;
                      const isOddIndex = index % 2 === 1;
                      const isText = type === 'Text';
                      const isNumber = type === 'Number';
                      const isCheckBox = type === 'CheckBox';
                      const isSelect = type === 'Select';

                      return (
                        <Col
                          key={id}
                          span={24}
                          lg={isOddLength && isLastIndex ? 24 : 12}
                          className={cn(
                            isOddIndex ? 'lg:ps-3' : 'lg:pe-3',
                            isOddLength && isLastIndex && 'lg:p-0',
                          )}
                        >
                          <Form.Item<ModalFieldType>
                            label={name}
                            name={`variable-attribute-${id}`}
                            rules={
                              isText
                                ? [inputRule.required, inputRule.string]
                                : isNumber
                                  ? [inputRule.required, inputRule.digit]
                                  : [inputRule.required]
                            }
                          >
                            {isText || isNumber ? (
                              <Input placeholder={`${name} مدل را وارد کنید`} />
                            ) : isCheckBox || isSelect ? (
                              <Select
                                showSearch
                                allowClear
                                maxTagCount='responsive'
                                mode={isCheckBox ? 'multiple' : undefined}
                                placeholder={`${name} مدل را انتخاب کنید`}
                                options={values.map(value => ({ value }))}
                              />
                            ) : undefined}
                          </Form.Item>
                        </Col>
                      );
                    },
                  )}
                  <Col span={24}>
                    <Divider dashed className='mb-6 mt-1' />
                  </Col>
                </React.Fragment>
              ) : null}
            </React.Fragment>
            <Col span={24} lg={12} className='lg:pe-3'>
              <Form.Item<ModalFieldType>
                label='شناسه'
                name='identifier'
                rules={[inputRule.nonDot]}
              >
                <Input placeholder='شناسه مدل را وارد کنید' />
              </Form.Item>
            </Col>
            <Col span={24} lg={12} className='lg:ps-3'>
              <Form.Item<ModalFieldType>
                label='توضیحات مختصر'
                name='abstraction'
                rules={[inputRule.string, inputRule.abstraction]}
              >
                <Input.TextArea
                  autoSize
                  placeholder='توضیحات مختصر مدل را وارد کنید'
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Divider dashed className='mb-6 mt-1' />
            </Col>
            <Col span={24} lg={12} className='lg:pe-3'>
              <Form.Item<ModalFieldType>
                label='قیمت اولیه'
                name='originalPrice'
                rules={[inputRule.required, inputRule.digit]}
              >
                <DecimalInputNumber
                  placeholder='قیمت اولیه مدل را وارد کنید'
                  addonAfter={PRICE}
                />
              </Form.Item>
            </Col>
            <Col span={24} lg={12} className='lg:ps-3'>
              <Form.Item<ModalFieldType>
                label='قیمت فروش (به مشتری)'
                name='price'
                rules={[inputRule.digit]}
              >
                <DecimalInputNumber
                  placeholder='قیمت فروش مدل را وارد کنید'
                  addonAfter={PRICE}
                />
              </Form.Item>
            </Col>
            <Col span={24} lg={12} className='lg:pe-3'>
              <Form.Item<ModalFieldType>
                label='حداقل سفارش'
                name='purchaseMinStock'
                rules={[inputRule.digit]}
              >
                <DecimalInputNumber placeholder='حداقل سفارش مدل را وارد کنید' />
              </Form.Item>
            </Col>
            <Col span={24} lg={12} className='lg:ps-3'>
              <Form.Item<ModalFieldType>
                label='حداکثر سفارش'
                name='purchaseMaxStock'
                rules={[inputRule.digit]}
              >
                <DecimalInputNumber placeholder='حداکثر سفارش مدل را وارد کنید' />
              </Form.Item>
            </Col>
            <Col span={24} lg={12} className='lg:pe-3'>
              <Form.Item<ModalFieldType>
                label='موجودی انبار'
                name='stock'
                rules={[inputRule.digit]}
              >
                <DecimalInputNumber placeholder='موجودی انبار مدل را وارد کنید' />
              </Form.Item>
            </Col>
            <Col span={24} lg={12} className='lg:ps-3'>
              <Form.Item<ModalFieldType>
                label='فعال‌سازی انبار'
                name='activateStock'
                valuePropName='checked'
              >
                <CheckInputbox>فعال‌سازی انبار برای موجودی</CheckInputbox>
              </Form.Item>
            </Col>
            <Col span={24} lg={12} className='lg:pe-3'>
              <Form.Item<ModalFieldType>
                label='فعال‌سازی اسنپ‌پی'
                name='activeSnapPay'
                valuePropName='checked'
              >
                <CheckInputbox>
                  فعال‌سازی اسنپ‌پی برای درگاه پرداخت
                </CheckInputbox>
              </Form.Item>
            </Col>
            <Col span={24} lg={12} className='lg:ps-3'>
              <Form.Item<ModalFieldType>
                label='فعال‌سازی ترب‌پی'
                name='activeTorobPay'
                valuePropName='checked'
              >
                <CheckInputbox>
                  فعال‌سازی ترب‌پی برای درگاه پرداخت
                </CheckInputbox>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Divider dashed className='mb-6 mt-1' />
            </Col>
            <Col span={24} lg={12} className='lg:pe-3'>
              <Form.Item<ModalFieldType>
                label='ارسال رایگان'
                name='freeShipping'
                valuePropName='checked'
              >
                <CheckInputbox>فعال‌سازی ارسال رایگان</CheckInputbox>
              </Form.Item>
            </Col>
            <Col span={24} lg={12} className='lg:pe-3'>
              <Form.Item<ModalFieldType>
                label='ارسال فوری'
                name='expressShipping'
                valuePropName='checked'
              >
                <CheckInputbox>فعال‌سازی ارسال فوری</CheckInputbox>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Divider dashed className='mb-6 mt-1' />
            </Col>
            <Col span={24} lg={12} className='lg:pe-3'>
              <Form.Item<ModalFieldType>
                label='طول'
                name='length'
                rules={[inputRule.digit]}
              >
                <DecimalInputNumber
                  placeholder='طول مدل را وارد کنید'
                  addonAfter={WIDTH}
                />
              </Form.Item>
            </Col>
            <Col span={24} lg={12} className='lg:ps-3'>
              <Form.Item<ModalFieldType>
                label='عرض'
                name='width'
                rules={[inputRule.digit]}
              >
                <DecimalInputNumber
                  placeholder='عرض مدل را وارد کنید'
                  addonAfter={WIDTH}
                />
              </Form.Item>
            </Col>
            <Col span={24} lg={12} className='lg:pe-3'>
              <Form.Item<ModalFieldType>
                label='ارتفاع'
                name='height'
                rules={[inputRule.digit]}
              >
                <DecimalInputNumber
                  placeholder='ارتفاع مدل را وارد کنید'
                  addonAfter={WIDTH}
                />
              </Form.Item>
            </Col>
            <Col span={24} lg={12} className='lg:ps-3'>
              <Form.Item<ModalFieldType>
                label='وزن'
                name='weight'
                rules={[inputRule.digit]}
              >
                <DecimalInputNumber
                  placeholder='وزن مدل را وارد کنید'
                  addonAfter={WEIGHT}
                />
              </Form.Item>
            </Col>
          </Row>
        </PrimitiveForm>
        <Divider />
      </Modal>
    );
  },
);
ProductModelsInfoModal.displayName = 'ProductModelsInfoModal';

const ProductModelsInfo = () => {
  const [open, { set: setOpen }] = useBoolean();
  const {
    data: categoryData,
    loading: categoryLoading,
    run: runCategory,
  } = useRequest(getCategory, {
    manual: true,
  });
  const {
    mainCategory,
    productType,
    subProducts = [],
    variableAttributes = [],
  } = useProductValue();
  const action = useProductAction();

  useMount(() => {
    if (mainCategory) {
      const { id } = mainCategory;
      runCategory({ id });
    }
  });

  const isNotVariableProduct = productType !== 'Variable';
  const disableCreateSubProduct =
    productType === 'Normal'
      ? subProducts.length >= 1
      : productType === 'Variable'
        ? variableAttributes.length === 0
        : true;

  const totalAttributes = categoryData?.attributes ?? [];
  const attributesSelectOptions = generateSelectOptions(
    totalAttributes,
    'id',
    'name',
    'id',
  );

  return (
    <Row>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='نوع محصول'
          name='productType'
          rules={[inputRule.required]}
        >
          <Select<SelectOption>
            labelInValue
            placeholder='نوع محصول را انتخاب کنید'
            options={productTypes.map(type => ({
              label: localizeProductType(type).label,
              value: type,
            }))}
            onChange={({ value }) => {
              action({
                type: 'UPDATE_PRODUCT_TYPE',
                payload: {
                  productType: value as typeof productType,
                },
              });
              action({
                type: 'UPDATE_SUB_PRODUCTS',
                payload: {
                  subProducts: undefined,
                  selectedSubProduct: undefined,
                },
              });
            }}
          />
        </Form.Item>
      </Col>

      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType>
          label='فیلدهای متغیر'
          name='variableAttributes'
          rules={!isNotVariableProduct ? [inputRule.required] : undefined}
        >
          <Select<SelectOption[]>
            labelInValue
            mode='multiple'
            maxTagCount='responsive'
            className='text-[--ant-color-primary-active]'
            placeholder='فیلدهای متغیر محصول را انتخاب کنید'
            disabled={isNotVariableProduct}
            loading={categoryLoading}
            options={attributesSelectOptions}
            filterOption={(inputValue, option) =>
              normalizeToString(option?.label)
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            }
            onChange={values => {
              const selectedAttributes = totalAttributes.filter(attribute =>
                values.some(({ value }) => attribute.id === value),
              );

              action({
                type: 'UPDATE_PRODUCT_VARIABLE_ATTRIBUTES',
                payload: {
                  variableAttributes: selectedAttributes,
                },
              });
              action({
                type: 'UPDATE_SUB_PRODUCTS',
                payload: {
                  subProducts: undefined,
                  selectedSubProduct: undefined,
                },
              });
            }}
            {...(!mainCategory && {
              notFoundContent: (
                <Empty
                  className='my-3'
                  description='ابتدا دسته‌بندی اصلی را انتخاب کنید.'
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  imageStyle={{
                    height: 'calc(var(--ant-control-height-lg) * 0.875)',
                  }}
                />
              ),
            })}
          />
        </Form.Item>
      </Col>

      <Col span={24}>
        <Divider dashed className='mb-5 mt-2' />
      </Col>

      <Col span={24}>
        <Flex
          align='center'
          justify='space-between'
          gap='small'
          className='max-sm:flex-wrap'
        >
          <Typography.Title level={4} className='mb-0'>
            مدل‌ها
          </Typography.Title>
          <Button
            className='max-sm:basis-full'
            icon={<PlusOutlinedIcon />}
            disabled={disableCreateSubProduct}
            onClick={() => setOpen(true)}
          >
            ساخت مدل
          </Button>
          <ProductModelsInfoModal
            open={open}
            closeModal={() => setOpen(false)}
          />
        </Flex>

        <div className='my-4 overflow-auto'>
          {subProducts.length > 0 ? (
            <DndSortableContext
              dnd={{
                onDragEnd: ({ active, over }) => {
                  if (active.id !== over?.id) {
                    const activeIndex = subProducts.findIndex(
                      ({ id }) => id === active.id,
                    );
                    const overIndex = subProducts.findIndex(
                      ({ id }) => id === over?.id,
                    );
                    const sortedSubProducts = dndArrayMove(
                      subProducts,
                      activeIndex,
                      overIndex,
                    );

                    action({
                      type: 'UPDATE_SUB_PRODUCTS',
                      payload: {
                        subProducts: sortedSubProducts,
                        selectedSubProduct: undefined,
                      },
                    });
                  }
                },
              }}
              sortable={{
                items: subProducts.map(({ id }) => id),
                children: subProducts.map(subProduct => (
                  <ProductModelsInfoSubProduct
                    key={subProduct.id}
                    openModal={() => setOpen(true)}
                    {...subProduct}
                  />
                )),
              }}
            />
          ) : (
            <Empty
              className='my-20'
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span className='text-zinc-300'>هنوز مدلی ساخته نشده است.</span>
              }
            />
          )}
        </div>
      </Col>
    </Row>
  );
};

export {
  ProductModelsInfo,
  ProductModelsInfoModal,
  ProductModelsInfoSubProduct,
};
